import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import AppLayout from '../components/AppLayout';
import './Dashboard.css';

function formatAmount(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function Dashboard() {
  const { token, user } = useAuth();
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get('/api/bank/account', { headers }),
      axios.get('/api/bank/transactions?limit=20', { headers }),
    ])
      .then(([accRes, txRes]) => {
        setAccount(accRes.data);
        const all = txRes.data.transactions || [];
        setTransactions(all.filter(tx => tx.type !== 'salary' && tx.type !== 'salaire').slice(0, 5));
      })
      .catch(e => setError(e.response?.data?.error || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <AppLayout>
        <div className="dash-loading">
          <i className="fa-solid fa-spinner spinner" style={{ fontSize: 32, color: 'var(--primary)' }} />
          <span>Chargement de votre compte...</span>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="dash-error">
          <i className="fa-solid fa-circle-exclamation" />
          <p>{error}</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Page header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">Bonjour, {user?.characterName?.split(' ')[0] || 'Client'}</h1>
          <p className="dash-subtitle">Voici un apercu de votre compte bancaire</p>
        </div>
        <span className="badge badge-primary">
          <i className="fa-solid fa-circle" style={{ fontSize: 8 }} />
          En ligne
        </span>
      </div>

      {/* Balance card */}
      <div className="balance-card">
        <div className="balance-card-bg" />
        <div className="balance-card-content">
          <div className="balance-card-top">
            <div>
              <span className="balance-label-text">Solde disponible</span>
              <div className="balance-amount">{formatAmount(account?.balance || 0)}</div>
            </div>
            <div className="balance-bank-icon">
              <i className="fa-solid fa-building-columns" />
            </div>
          </div>
          <div className="balance-card-bottom">
            <div className="balance-info-item">
              <span className="balance-info-label">Titulaire</span>
              <span className="balance-info-value">{account?.characterName}</span>
            </div>
            <div className="balance-info-divider" />
            <div className="balance-info-item">
              <span className="balance-info-label">IBAN</span>
              <span className="balance-info-value iban-text">{account?.iban}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="quick-actions">
        <Link to="/transfers" className="quick-action-btn">
          <div className="quick-action-icon">
            <i className="fa-solid fa-paper-plane" />
          </div>
          <span>Virement</span>
        </Link>
        <Link to="/history" className="quick-action-btn">
          <div className="quick-action-icon quick-action-icon-secondary">
            <i className="fa-solid fa-clock-rotate-left" />
          </div>
          <span>Historique</span>
        </Link>
        <Link to="/company" className="quick-action-btn">
          <div className="quick-action-icon quick-action-icon-neutral">
            <i className="fa-solid fa-briefcase" />
          </div>
          <span>Entreprise</span>
        </Link>
        <Link to="/invoices" className="quick-action-btn">
          <div className="quick-action-icon quick-action-icon-neutral">
            <i className="fa-solid fa-file-invoice-dollar" />
          </div>
          <span>Factures</span>
        </Link>
      </div>

      {/* Recent transactions */}
      <div className="dash-section">
        <div className="dash-section-header">
          <h2 className="dash-section-title">
            <i className="fa-solid fa-clock-rotate-left" />
            Transactions recentes
          </h2>
          <Link to="/history" className="dash-see-all">
            Voir tout <i className="fa-solid fa-chevron-right" />
          </Link>
        </div>

        {transactions.length === 0 ? (
          <div className="empty-state">
            <i className="fa-solid fa-receipt" />
            <p>Aucune transaction pour le moment</p>
            <span>Vos transactions apparaitront ici</span>
          </div>
        ) : (
          <div className="transaction-list">
            {transactions.map(tx => (
              <TransactionRow key={tx.id} tx={tx} userIban={account?.iban} userName={account?.characterName} />
            ))}
          </div>
        )}
      </div>

      {/* Account info card */}
      <div className="card account-info-card">
        <h3 className="account-info-title">
          <i className="fa-solid fa-circle-info" />
          Informations du compte
        </h3>
        <div className="account-info-grid">
          <div className="account-info-row">
            <span className="account-info-label">Titulaire</span>
            <span className="account-info-value">{account?.characterName}</span>
          </div>
          <div className="account-info-row">
            <span className="account-info-label">IBAN</span>
            <span className="account-info-value" style={{ fontFamily: 'monospace', fontSize: 13 }}>{account?.iban}</span>
          </div>
          <div className="account-info-row">
            <span className="account-info-label">Banque</span>
            <span className="account-info-value">Fleeca Bank</span>
          </div>
          <div className="account-info-row">
            <span className="account-info-label">Statut</span>
            <span className="badge badge-success">
              <i className="fa-solid fa-circle-check" />
              Actif
            </span>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function TransactionRow({ tx, userIban, userName }) {
  const isSender = userIban && tx.from_iban === userIban;
  const isDebit = tx.type === 'debit' || tx.type === 'withdrawal' || (tx.type === 'transfer' && isSender);

  const formatIban = (iban) => {
    if (!iban) return null;
    const clean = iban.replace(/\s/g, '');
    return clean.length > 8 ? clean.slice(0, 2) + '…' + clean.slice(-6) : clean;
  };

  const myName = userName || tx.character_name || 'Moi';
  const counterpartIban = isDebit ? tx.to_iban : tx.from_iban;
  const counterpartName = tx.counterpart_name || formatIban(counterpartIban) || 'Système';

  let senderName, receiverName;
  if (tx.type === 'salary' || tx.type === 'credit' || (!isSender && tx.type === 'transfer')) {
    senderName = counterpartName;
    receiverName = myName;
  } else {
    senderName = myName;
    receiverName = counterpartName;
  }

  const TYPE_LABELS = ['Debit','Credit','Virement','Depot','Retrait','Salaire'];
  const isRedundantLabel = !tx.label || tx.label.includes('→') || tx.label.includes('->') || TYPE_LABELS.includes(tx.label);

  return (
    <div className="transaction-row">
      <div className={`transaction-icon ${isDebit ? 'tx-debit' : 'tx-credit'}`}>
        <i className={`fa-solid ${isDebit ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
      </div>
      <div className="transaction-info">
        <span className="transaction-label" style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 800 }}>{senderName}</span>
          <i className="fa-solid fa-arrow-right" style={{ fontSize: 10, color: 'var(--on-surface-variant)' }} />
          <span style={{ fontWeight: 800 }}>{receiverName}</span>
        </span>
        {!isRedundantLabel && (
          <span style={{ display: 'block', fontSize: 11, color: 'var(--on-surface-variant)', fontWeight: 500 }}>{tx.label}</span>
        )}
        <span className="transaction-date">{formatDate(tx.created_at)}</span>
        {tx.id && (
          <span className="tx-id" title={tx.id}>
            <i className="fa-solid fa-hashtag" />{tx.id.slice(0, 8)}
          </span>
        )}
      </div>
      <div className={`transaction-amount ${isDebit ? 'amount-debit' : 'amount-credit'}`}>
        {isDebit ? '-' : '+'}{formatAmount(Math.abs(tx.amount))}
      </div>
    </div>
  );
}
